/**
 * A stable fingerprint of a GetProjectlist response, used to decide whether a
 * QUIET background refetch has anything worth telling the UI about.
 *
 * WHAT IS SIGNED, AND WHY IT IS A FIELD LIST RATHER THAN THE WHOLE PAYLOAD.
 *
 * This used to be `JSON.stringify(rawPayload)` — everything the server said. That
 * is the conservative choice and it was deliberate: a signature computed from a
 * hand-written field list can omit a field by oversight, and an omitted field is a
 * row that silently displays stale text. But whole-payload signing has a fatal
 * property in this application: `lastTelemetryEpochMs` ADVANCES ON EVERY TICK of a
 * running cure, so while any cure in the tenant is live the signature changes on
 * essentially every refetch and the grid rebuilds — 69 rows, 80 times an hour,
 * MEASURED. The suppression collapsed exactly when it was needed, and a tab
 * viewing a FINISHED cure paid the whole cost for someone else's live one.
 *
 * So the list below is explicit, and the rule for maintaining it is:
 * SIGN ANYTHING THE GRID RENDERS, plus anything that decides which SECTION a row
 * lands in. Getting that wrong in the cautious direction costs a needless rebuild;
 * getting it wrong the other way means a row silently shows stale text, which is
 * the failure this whole change exists to remove. If you add a column, add it here.
 *
 * VERIFIED AGAINST THE TEMPLATE, not assumed: `project-grid-view.ts` renders
 * ProjectName, SerialNumber, CustomerName, Version, FiberType, DeviceName,
 * UserParameter and LastTelemetryReceived, and splits Live/Historical on isLive.
 *
 * ALIAS-TOLERANT ON PURPOSE. The payload has three spellings for most fields
 * (`projectName` / `PNAME` / `Name` / `ProjectName`, and so on) and
 * ProjectService's mapper accepts all of them. Reading only one spelling here
 * would yield `undefined` for every row, which is a CONSTANT signature — and a
 * constant signature suppresses every update forever. That is the worst available
 * failure and it would look exactly like "the server stopped sending changes".
 */

/** Row shape as it arrives — every field optional, every field multiply spelled. */
type RawProjectRow = Record<string, unknown>;

function firstOf(row: RawProjectRow, ...names: string[]): unknown {
  for (const n of names) {
    const v = row[n];
    if (v !== undefined && v !== null) return v;
  }
  return null;
}

/**
 * The telemetry epoch, BUCKETED TO THE MINUTE.
 *
 * The one field that cannot be signed raw — see the header. It is also the one
 * that cannot simply be dropped, because the grid RENDERS it: `LastTelemetryReceived`
 * is both a display column and a sort key (`project-grid-view.ts` formatProjectDate
 * / sortValue).
 *
 * A minute bucket means a republish is suppressed until the minute rolls over, so
 * the cell can trail the truth by up to 59 s. It is never WRONG — it shows the last
 * value that was published, not a fabricated one — it is simply not the newest.
 * That trade is deliberate: the column answers "roughly when did we last hear from
 * this device", and the ONLINE/OFFLINE decision is made by `deviceStatus` (three
 * empty polls AND 60 s of silence), never by a human reading this timestamp.
 *
 * NOTE the column renders to SECONDS precision, so this bucket is coarser than the
 * display. If that ever becomes a complaint, the fix is to render minutes too —
 * not to sign the raw epoch, which reinstates the rebuild storm.
 */
function telemetryMinuteBucket(row: RawProjectRow): number | null {
  const epoch = firstOf(row, 'lastTelemetryEpochMs', 'LastTelemetryEpochMs', 'lastTelemetryEpoch');
  if (typeof epoch === 'number' && Number.isFinite(epoch)) {
    return Math.floor(epoch / 60_000);
  }
  // Fall back to the stored string only when the derived epoch is absent, mirroring
  // ProjectInfo's own precedence. Unparseable stays null rather than becoming 0,
  // so "no telemetry" and "telemetry at the unix epoch" remain distinguishable.
  const stored = firstOf(row, 'lastTelemetryReceived', 'LTR', 'LastTelemetryReceived');
  if (typeof stored === 'string' && stored.length > 0) {
    const t = new Date(stored).getTime();
    return Number.isFinite(t) ? Math.floor(t / 60_000) : null;
  }
  return null;
}

/** The fields a change in which must reach the grid. Order is positional, not keyed. */
function signatureFieldsOf(row: unknown): unknown[] {
  if (row === null || typeof row !== 'object') {
    // A non-object row is a shape we do not understand. Signing it as its own JSON
    // keeps it CHANGE-DETECTING rather than collapsing several of them together.
    return ['?', JSON.stringify(row)];
  }
  const r = row as RawProjectRow;
  return [
    // IDENTITY — also the row-identity set: a project appearing or disappearing
    // changes the array of these, which per-row content alone would not catch.
    firstOf(r, 'projectName', 'PNAME', 'Name', 'ProjectName'),
    firstOf(r, 'serialNumber', 'SER', 'SerialNumber', 'DTSSerial'),
    // CLASSIFICATION — decides the Live/Historical split today, and `finalized`
    // becomes load-bearing under the four-state liveness model.
    firstOf(r, 'isLive', 'IsLive', 'live'),
    firstOf(r, 'finalized', 'Finalized', 'IsFinalized', 'isFinalized'),
    // TENANCY — should never change; a tenant repoint would, and silently.
    firstOf(r, 'customerId', 'CustomerId', 'customerID'),
    // RENDERED — everything else the grid puts on screen.
    firstOf(r, 'customerName', 'CustomerName'),
    firstOf(r, 'version', 'Version', 'MeasuredLinerVersion'),
    firstOf(r, 'fiberType', 'FiberType'),
    firstOf(r, 'deviceName', 'DeviceName'),
    firstOf(r, 'userParameter', 'UserParameter'),
    firstOf(r, 'samplingInterval', 'SamplingInterval'),
    firstOf(r, 'curingTemperature', 'CuringTemperature'),
    firstOf(r, 'overheatTemperature', 'OverheatTemperature'),
    firstOf(r, 'linerLength', 'LinerLength'),
    firstOf(r, 'cmsModel', 'CmsModel'),
    firstOf(r, 'cmsFirmware', 'CmsFirmware'),
    telemetryMinuteBucket(r),
  ];
}

export function projectListSignature(payload: unknown): string {
  if (!Array.isArray(payload)) {
    // A non-array is a failure shape, not a list. Give it a signature that can
    // never equal a real list's, so a malformed response can never be mistaken
    // for "unchanged" and silently suppressed.
    return 'not-a-list';
  }
  // Order matters and is deliberately NOT normalised away: the server sorts the
  // list, so a reordering is a real change the grid must render.
  return `list:${JSON.stringify(payload.map(signatureFieldsOf))}`;
}

/**
 * True when a quiet refetch may be dropped without telling anyone.
 *
 * Only ever consulted for QUIET refetches. A first load, a customer switch or a
 * retry must always emit, because something is waiting on that emission —
 * `restoreProjectOrShowPicker` takes exactly one and would wait out its timeout
 * without it.
 *
 * A null `previous` means nothing has been fingerprinted yet, which is never
 * "unchanged": the first response through this path must always be published.
 */
export function isUnchangedList(previous: string | null, next: string): boolean {
  return previous !== null && previous === next;
}
